import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Messages / Inbox page — lists all booking conversations
 * with last message preview, unread count, sorted by most recent.
 */
export default function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchConversations() {
      try {
        setLoading(true);
        const res = await api.get('/bookings/messages/conversations');
        setConversations(res.data.conversations || []);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchConversations();
  }, []);

  const statusLabels = {
    pending: 'En attente',
    accepted: 'Accepté',
    rejected: 'Refusé',
    cancelled: 'Annulé',
  };

  const statusColors = {
    pending: 'border-status-pending text-status-pending bg-status-pending/10',
    accepted: 'border-accent text-accent bg-accent/10',
    rejected: 'border-status-rejected text-status-rejected bg-status-rejected/10',
    cancelled: 'th-border th-text-muted',
  };

  function formatRelativeTime(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `il y a ${diffMins} min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    if (diffDays < 7) return `il y a ${diffDays}j`;

    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    });
  }

  function truncateMessage(content, maxLen = 80) {
    if (!content) return '';
    if (content.length <= maxLen) return content;
    return content.slice(0, maxLen) + '…';
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 animate-fadeIn">
        <h1 className="th-page-title mb-8">Messages</h1>
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 th-bg-surface border th-border animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="th-page-title mb-4">Messages</h1>
        <p className="th-text-secondary text-sm font-mono">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fadeIn">
      <h1 className="th-page-title mb-8">Messages</h1>

      {conversations.length === 0 ? (
        <div className="border th-border p-8 text-center th-bg-surface">
          <p className="th-text-muted text-sm font-mono">Aucune conversation pour le moment.</p>
          <p className="th-text-muted text-xs font-mono mt-2">
            Les messages apparaîtront ici quand vous échangerez avec un artiste ou promoteur via un booking.
          </p>
        </div>
      ) : (
        <div className="space-y-px dark:bg-dark-border bg-light-border">
          {conversations.map((conv) => {
            const isOwnMessage = conv.lastMessage.senderId === user?.id;
            const hasUnread = conv.unreadCount > 0;

            return (
              <Link
                key={conv.bookingId}
                to={`/bookings/${conv.bookingId}?tab=messages`}
                className={`block th-bg p-4 hover:bg-accent/5 transition-colors duration-150 ${
                  hasUnread ? 'border-l-2 border-l-accent' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  {/* Left: conversation info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`font-mono text-sm truncate ${hasUnread ? 'font-semibold th-text' : 'th-text'}`}>
                        {conv.eventTitle || 'Événement'}
                      </p>
                      <span className={`th-badge text-[10px] ${statusColors[conv.bookingStatus]}`}>
                        {statusLabels[conv.bookingStatus]}
                      </span>
                    </div>
                    <p className="text-xs th-text-muted font-mono mb-1.5">
                      {conv.artistName} · {conv.promoterCompanyName || 'Promoteur'}
                    </p>
                    <p className={`text-xs truncate ${hasUnread ? 'th-text font-medium' : 'th-text-secondary'}`}>
                      {isOwnMessage && (
                        <span className="th-text-muted">Vous : </span>
                      )}
                      {truncateMessage(conv.lastMessage.content)}
                    </p>
                  </div>

                  {/* Right: time + unread badge */}
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className="text-[10px] font-mono th-text-muted whitespace-nowrap">
                      {formatRelativeTime(conv.lastMessage.createdAt)}
                    </span>
                    {hasUnread && (
                      <span className="bg-accent text-white text-[10px] font-mono px-1.5 py-0.5 min-w-[20px] text-center">
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
